import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import type { ThemeOptions } from "@mui/material/styles";
import { getBrandedThemeOptions } from "@/theme/brandedThemeOptions";
import {
  ensureTenantBrandingLoaded,
  readBrandingStorageFingerprint,
  readStoredTenantBrandingIdentity,
  subscribeTenantBrandingUpdated,
  type TenantBrandingIdentity,
} from "@/utils/tenant-branding-cache";

export type TenantBrandingContextValue = {
  identity: TenantBrandingIdentity;
  themes: { light: ThemeOptions; dark: ThemeOptions };
};

const TenantBrandingContext = createContext<TenantBrandingContextValue | null>(null);

function readBrandingContextValue(): TenantBrandingContextValue {
  return {
    identity: readStoredTenantBrandingIdentity(),
    themes: getBrandedThemeOptions(),
  };
}

/**
 * Single source of truth for tenant branding in React.
 * Loads branding once per tab, fans out updates to all consumers.
 */
export function TenantBrandingProvider({ children }: { children: ReactNode }): ReactElement {
  const [value, setValue] = useState(readBrandingContextValue);
  const fingerprintRef = useRef(readBrandingStorageFingerprint());

  useEffect(() => {
    const unsubscribe = subscribeTenantBrandingUpdated(() => {
      const nextFingerprint = readBrandingStorageFingerprint();
      if (nextFingerprint === fingerprintRef.current) {
        return;
      }
      fingerprintRef.current = nextFingerprint;
      setValue(readBrandingContextValue());
    });

    void ensureTenantBrandingLoaded().then(() => {
      const nextFingerprint = readBrandingStorageFingerprint();
      if (nextFingerprint === fingerprintRef.current) {
        return;
      }
      fingerprintRef.current = nextFingerprint;
      setValue(readBrandingContextValue());
    });

    return unsubscribe;
  }, []);

  return <TenantBrandingContext.Provider value={value}>{children}</TenantBrandingContext.Provider>;
}

export function useTenantBrandingContext(): TenantBrandingContextValue {
  const context = useContext(TenantBrandingContext);
  if (!context) {
    return readBrandingContextValue();
  }
  return context;
}
